export const ACHIEVEMENT_TRANSFER_SCHEMA = "xpanel.achievement.transfer";
export const ACHIEVEMENT_TRANSFER_VERSION = 1;

const SOURCE_TYPE_OPTIONS = [
	{ value: "deposit", label: "Deposit" },
	{ value: "withdrawal", label: "Withdrawal" },
	{ value: "verify", label: "Verify" },
	{ value: "email_verified", label: "Email verified" },
	{ value: "phone_verified", label: "Phone verified" },
	{ value: "tg_verified", label: "Telegram verified" },
	{ value: "vip_lvl", label: "Vip lvl" },
	{ value: "spins", label: "Spins" },
	{ value: "mobile", label: "Mobile" },
	{ value: "referral", label: "Referral" },
	{ value: "tasks", label: "Tasks" },
];

const TYPE_OPTIONS = [
	{ value: "daily", label: "daily" },
	{ value: "unique", label: "unique" },
	{ value: "progressive", label: "progressive" },
	{ value: "daily_progressive", label: "daily progressive" },
];

const REWARD_TYPE_OPTIONS = [
	{ value: "money", label: "Money" },
	{ value: "tickets", label: "Tickets" },
	{ value: "freespins", label: "Freespins" },
	{ value: "chest", label: "Chest" },
];

const ACTIVE_OPTIONS = [
	{ value: "1", label: "Enabled" },
	{ value: "0", label: "Disabled" },
];

const CYCLE_OPTIONS = [
	{ value: "replay", label: "Replay" },
	{ value: "not", label: "Not repeatable" },
];

const CYCLE_UNIT_OPTIONS = [
	{ value: "hour", label: "Hour" },
	{ value: "day", label: "Day" },
];

const TRIGGER_TYPE_OPTIONS = [
	{ value: "crypto", label: "Crypto" },
	{ value: "fiat", label: "Fiat" },
	{ value: "both", label: "Both" },
];

const ENDORPHINA_PROVIDER = { label: "Endorphina", value: "endorphina", type: 10 };

export const safeJsonParse = (value, fallback = null) => {
	if (value === null || typeof value === "undefined" || value === "") return fallback;
	if (typeof value !== "string") return value;
	try {
		return JSON.parse(value);
	} catch {
		return fallback;
	}
};

export const getOption = (options, value) => {
	if (value === null || typeof value === "undefined" || value === "") return null;
	return options.find((option) => String(option.value) === String(value)) || null;
};

export const normalizeAchievementImagePath = (image) => {
	if (!image || typeof image !== "string") return "";
	if (image.startsWith("blob:") || image.includes("base64")) return "";
	return image;
};

export const validateAchievementTransferData = (data) => {
	if (!data || typeof data !== "object") {
		throw new Error("Invalid achievement JSON data");
	}

	if (data.image && (typeof data.image !== "string" || !data.image.startsWith("https://"))) {
		throw new Error("Achievement JSON image must be a full https:// URL");
	}
};

export const buildAchievementTransferEnvelope = (data) => ({
	schema: ACHIEVEMENT_TRANSFER_SCHEMA,
	version: ACHIEVEMENT_TRANSFER_VERSION,
	data,
});

export const parseAchievementTransferEnvelope = (jsonText) => {
	const parsed = JSON.parse(jsonText);
	if (!parsed || parsed.schema !== ACHIEVEMENT_TRANSFER_SCHEMA || parsed.version !== ACHIEVEMENT_TRANSFER_VERSION || !parsed.data || typeof parsed.data !== "object") {
		throw new Error("Invalid achievement JSON schema or version");
	}
	validateAchievementTransferData(parsed.data);
	return parsed.data;
};

export const stringifyAchievementTransfer = (data) => JSON.stringify(buildAchievementTransferEnvelope(data), null, 2);

export const getAchievementTransferJson = (data) => {
	validateAchievementTransferData(data);
	return stringifyAchievementTransfer(data);
};

const normalizeComparableValue = (value) => {
	if (value === undefined) return null;
	if (value && typeof value === "object" && !Array.isArray(value)) {
		if (Object.prototype.hasOwnProperty.call(value, "value")) return value.value;
	}
	return value;
};

export const findChangedLockedAchievementFields = (currentData, editedData) => {
	const lockedFields = [
		"type",
		"sourceType",
		"count",
		"rewardType",
		"rewardValue",
		"rewardConditions",
		"spinsConditions",
		"cycle",
		"cycleTimer",
		"cycleUnit",
		"cohort",
		"levels",
		"amount",
		"triggerType",
		"dailyProgressiveConfig",
	];

	return lockedFields.filter((field) => {
		if (!Object.prototype.hasOwnProperty.call(editedData || {}, field)) return false;
		return JSON.stringify(normalizeComparableValue(currentData?.[field])) !== JSON.stringify(normalizeComparableValue(editedData?.[field]));
	});
};

const selectValue = (option) => option?.value || "";
const activeValue = (option) => (String(option?.value) === "1" || option === true || option === 1 ? 1 : 0);

const getGameId = (gameSelected) =>
	gameSelected?.gameId || gameSelected?.game?.id || gameSelected?.game?.gameID || gameSelected?.id || null;

const getProviderId = (gameSelected) =>
	gameSelected?.game?.providerID || gameSelected?.providerID || null;

export const buildCurrentRewardConditions = (achievement) => {
	const rewardType = selectValue(achievement.rewardType) || achievement.rewardType || "";
	const existingRewardConditions = safeJsonParse(achievement.rewardConditions, achievement.rewardConditions || {});

	if (rewardType === "chest") {
		const chestPrizeId = achievement.chestPrizeId || achievement.chestPrizeSelected?.chestPrize?.id || existingRewardConditions?.chestPrizeId;

		if (chestPrizeId) {
			return { chestPrizeId: Number(chestPrizeId) };
		}

		return existingRewardConditions || {};
	}

	if (rewardType === "freespins") {
		const gameId = getGameId(achievement.gameSelected) || existingRewardConditions?.gameId;
		const providerId = getProviderId(achievement.gameSelected) || existingRewardConditions?.providerId;
		const betAmount = achievement.betAmount !== "" && achievement.betAmount !== null && typeof achievement.betAmount !== "undefined"
			? parseFloat(achievement.betAmount) || 0
			: existingRewardConditions?.betAmount;
		const expiresAt = achievement.expiresAt !== "" && achievement.expiresAt !== null && typeof achievement.expiresAt !== "undefined"
			? parseInt(achievement.expiresAt, 10) || 0
			: existingRewardConditions?.expiresAt;

		return {
			gameId,
			providerId,
			betAmount,
			currency: existingRewardConditions?.currency || "RUB",
			expiresAt,
		};
	}

	return {};
};

const cloneDailyProgressiveConfig = (config) => {
	if (!config || typeof config !== "object") return config || null;

	return {
		breakOnMissedDay: config.breakOnMissedDay ?? false,
		resetType: config.resetType || "full",
		penaltyDays: config.penaltyDays || 0,
		dailyConditions: {
			type: config.dailyConditions?.type || "deposit",
			deposit: {
				amount: config.dailyConditions?.deposit?.amount || 0,
				paymentType: config.dailyConditions?.deposit?.paymentType || "both",
			},
			spins: {
				count: config.dailyConditions?.spins?.count || 0,
				minBet: config.dailyConditions?.spins?.minBet || 0,
				gameId: config.dailyConditions?.spins?.gameId || null,
				providerId: config.dailyConditions?.spins?.providerId || null,
			},
		},
		milestones: (config.milestones || []).map((milestone) => ({
			...milestone,
			rewardConditions: milestone.rewardConditions || {},
		})),
	};
};

const normalizeDailyProgressiveConfigForTransfer = (rawConfig, uiConfig) => {
	if (uiConfig) {
		return cloneDailyProgressiveConfig(uiConfig);
	}

	const parsedConfig = safeJsonParse(rawConfig, rawConfig || null);
	if (!parsedConfig || typeof parsedConfig !== "object") return null;

	const clonedConfig = cloneDailyProgressiveConfig(parsedConfig);

	if (clonedConfig.dailyConditions.type === "deposit" || clonedConfig.dailyConditions.type === "combined") {
		clonedConfig.dailyConditions.deposit.amount = clonedConfig.dailyConditions.deposit.amount
			? clonedConfig.dailyConditions.deposit.amount / 100
			: 0;
	}

	clonedConfig.milestones = clonedConfig.milestones.map((milestone) => ({
		...milestone,
		rewardValue: milestone.rewardType === "money"
			? Number(milestone.rewardValue || 0) / 100
			: milestone.rewardValue,
	}));

	return clonedConfig;
};

export const buildAchievementTransferData = (achievement, dailyProgressiveConfig) => {
	const triggerConditions = safeJsonParse(achievement.triggerConditions, achievement.triggerConditions || {});
	const rewardConditions = buildCurrentRewardConditions(achievement);
	const levelsConditions = safeJsonParse(achievement.levelsConditions, achievement.levels || []);
	const cohortConditions = safeJsonParse(achievement.cohortConditions, achievement.conditions || []);
	const dailyProgressiveConfigRaw = normalizeDailyProgressiveConfigForTransfer(
		achievement.dailyProgressiveConfigRaw || achievement.dailyProgressiveConfig,
		dailyProgressiveConfig
	);

	return {
		name: achievement.name || "",
		description: achievement.description || "",
		nameTranslationType: achievement.nameTranslationType || "",
		descriptionTranslationType: achievement.descriptionTranslationType || "",
		buttonTranslationType: achievement.buttonTranslationType || "",
		type: selectValue(achievement.type) || achievement.type || "",
		sourceType: selectValue(achievement.sourceType) || achievement.sourceType || "",
		count: Number(achievement.count) || 0,
		rewardType: selectValue(achievement.rewardType) || achievement.rewardType || "",
		rewardValue: Number(achievement.rewardValue) || 0,
		image: normalizeAchievementImagePath(achievement.image),
		link: achievement.link || "",
		active: activeValue(achievement.active),
		buttonText: achievement.buttonText || "",
		cycle: selectValue(achievement.cycle) || achievement.cycle || null,
		cycleUnit: selectValue(achievement.cycleUnit) || achievement.cycleUnit || null,
		cycleTimer: Number(achievement.cycleTimer) || 0,
		order: Number(achievement.order) || 0,
		startDate: achievement.startDate || achievement.start_time || null,
		endDate: achievement.endDate || achievement.end_time || null,
		amount: Number(achievement.amount) || 0,
		triggerType: selectValue(achievement.triggerType) || achievement.triggerType || "",
		rewardConditions,
		spinsConditions: triggerConditions?.spins || {},
		cohort: Array.isArray(achievement.conditions) ? achievement.conditions : cohortConditions || [],
		levels: Array.isArray(achievement.levels) && achievement.levels.length ? achievement.levels : levelsConditions || [],
		dailyProgressiveConfig: dailyProgressiveConfigRaw,
	};
};

const findProviderOption = (providers, providerId) => (providers || []).find((provider) => Number(provider.type) === Number(providerId)) || null;

const findGameOption = (games, gameId, providerId) => {
	const game = (games || []).find((item) => Number(item.gameID || item.id) === Number(gameId) && (!providerId || Number(item.providerID) === Number(providerId)));
	if (!game) return null;
	return {
		label: game.name || game.description,
		value: game.name || game.description,
		gameId: game.gameID || game.id,
		game: {
			id: game.gameID || game.id,
			description: game.name || game.description,
			name: game.name || game.description,
			gameID: game.gameID || game.id,
			providerID: game.providerID,
		},
	};
};

const findChestPrizeOption = (options, chestPrizeId) => {
	if (!chestPrizeId) return null;
	return (options || []).find((option) => Number(option.value) === Number(chestPrizeId)) || null;
};

const getChestRewardsPayload = (rewardConditions) => {
	const parsed = safeJsonParse(rewardConditions, []);
	if (Array.isArray(parsed)) return parsed;
	if (Array.isArray(parsed?.prizes)) return parsed.prizes;
	return [];
};

export const buildAddAchievementStateFromTransfer = (current, data, config, games) => {
	const rewardConditions = data.rewardConditions || {};
	const spinsConditions = data.spinsConditions || {};
	const freespinsProvider = rewardConditions.providerId ? ENDORPHINA_PROVIDER : null;
	const freespinsGame = rewardConditions.gameId ? findGameOption(games, rewardConditions.gameId, rewardConditions.providerId) : null;
	const spinsProvider = spinsConditions.providerId ? findProviderOption(config?.game?.options?.provider, spinsConditions.providerId) : null;
	const spinsGame = spinsConditions.gameId ? findGameOption(games, spinsConditions.gameId, spinsConditions.providerId) : null;
	const chestPrizeSelected = findChestPrizeOption(current.chestPrizeOptions, rewardConditions.chestPrizeId);
	const chestPrize = chestPrizeSelected?.chestPrize || null;

	return {
		...current,
		name: data.name ?? current.name,
		description: data.description ?? current.description,
		nameTranslationType: data.nameTranslationType ?? current.nameTranslationType,
		descriptionTranslationType: data.descriptionTranslationType ?? current.descriptionTranslationType,
		buttonTranslationType: data.buttonTranslationType ?? current.buttonTranslationType,
		type: getOption(TYPE_OPTIONS, data.type) || current.type,
		sourceType: getOption(SOURCE_TYPE_OPTIONS, data.sourceType) || current.sourceType,
		count: typeof data.count !== "undefined" ? data.count : current.count,
		rewardType: getOption(REWARD_TYPE_OPTIONS, data.rewardType) || current.rewardType,
		rewardValue: typeof data.rewardValue !== "undefined" ? data.rewardValue : current.rewardValue,
		link: data.link ?? current.link,
		active: getOption(ACTIVE_OPTIONS, data.active ? "1" : "0") || current.active,
		buttonText: data.buttonText ?? current.buttonText,
		cycle: getOption(CYCLE_OPTIONS, data.cycle) || current.cycle,
		cycleUnit: getOption(CYCLE_UNIT_OPTIONS, data.cycleUnit) || current.cycleUnit,
		cycleTimer: typeof data.cycleTimer !== "undefined" ? data.cycleTimer : current.cycleTimer,
		order: typeof data.order !== "undefined" ? data.order : current.order,
		startDate: data.startDate ?? current.startDate,
		endDate: data.endDate ?? current.endDate,
		amount: typeof data.amount !== "undefined" ? data.amount : current.amount,
		triggerType: getOption(TRIGGER_TYPE_OPTIONS, data.triggerType) || current.triggerType,
		conditions: Array.isArray(data.cohort) ? data.cohort : current.conditions || [],
		levels: Array.isArray(data.levels) ? data.levels : current.levels || [],
		providerSelected: freespinsProvider,
		gameSelected: freespinsGame,
		availableFreespinsGames: [],
		betAmount: rewardConditions.betAmount ?? current.betAmount,
		expiresAt: rewardConditions.expiresAt ?? current.expiresAt,
		chestPrizeId: rewardConditions.chestPrizeId ?? current.chestPrizeId,
		chestPrizeSelected: chestPrizeSelected || current.chestPrizeSelected,
		chestType: chestPrize?.type || current.chestType,
		chestRewards: chestPrize ? getChestRewardsPayload(chestPrize.rewardConditions) : current.chestRewards,
		providerSpinsSelected: spinsProvider,
		gameSpinsSelected: spinsGame,
		availableSpinsGames: [],
		gameSpinsAmount: spinsConditions.amount ?? current.gameSpinsAmount,
	};
};

export const buildCohortGroupsFromConditions = (conditions) => {
	const normalizedConditions = Array.isArray(conditions) ? conditions.filter(Boolean) : [];
	if (!normalizedConditions.length) {
		return [
			{
				groupId: crypto.randomUUID(),
				module: "deposit",
				groupKey: "depositFilters",
				groupLogicalOperator: null,
			},
		];
	}

	const groupsById = new Map();
	normalizedConditions.forEach((condition, index) => {
		const groupId = condition.groupId || crypto.randomUUID();
		if (groupsById.has(groupId)) return;
		groupsById.set(groupId, {
			groupId,
			module: condition.module || "profile",
			groupKey: condition.groupKey || "profileFilters",
			groupLogicalOperator: index === 0 ? null : condition.groupLogicalOperator || "AND",
			groupOrder: typeof condition.groupOrder === "number" ? condition.groupOrder : index,
		});
	});

	return [...groupsById.values()]
		.sort((a, b) => (a.groupOrder || 0) - (b.groupOrder || 0))
		.map((group, index) => ({
			groupId: group.groupId,
			module: group.module,
			groupKey: group.groupKey,
			groupLogicalOperator: index === 0 ? null : group.groupLogicalOperator || "AND",
		}));
};

export const buildEditAchievementStateFromTransfer = (current, data) => ({
	...current,
	name: data.name ?? current.name,
	description: data.description ?? current.description,
	nameTranslationType: data.nameTranslationType ?? current.nameTranslationType,
	descriptionTranslationType: data.descriptionTranslationType ?? current.descriptionTranslationType,
	buttonTranslationType: data.buttonTranslationType ?? current.buttonTranslationType,
	link: data.link ?? current.link,
	active: typeof data.active !== "undefined" ? getOption(ACTIVE_OPTIONS, data.active ? "1" : "0") : current.active,
	buttonText: data.buttonText ?? current.buttonText,
	order: typeof data.order !== "undefined" ? data.order : current.order,
	startDate: data.startDate ?? current.startDate,
	endDate: data.endDate ?? current.endDate,
});

export const copyTextToClipboard = async (value) => {
	if (navigator.clipboard && navigator.clipboard.writeText) {
		await navigator.clipboard.writeText(value);
		return;
	}

	const textarea = document.createElement("textarea");
	textarea.value = value;
	textarea.setAttribute("readonly", "");
	textarea.style.position = "absolute";
	textarea.style.left = "-9999px";
	document.body.appendChild(textarea);
	textarea.select();
	document.execCommand("copy");
	document.body.removeChild(textarea);
};
